import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { BarryHeader } from '~components/business-owner/barry/header/BarryHeader';
import { BarryIndividualConversation } from '~components/business-owner/barry/individual-conversation/BarryIndividualConversation';
import {
	BarrySideNavigation,
	type BarrySideNavigationProps,
} from '~components/business-owner/barry/side-navigation/BarrySideNavigation';
import { Popout, PopoutWidth } from '~components/commons/popout';

import { useIsMobile } from '~hooks/useIsMobile';

import { useGetConversationsForUser } from '~api/common/barry/queries/useGetConversationsForUser';
import { UIModeEnum, useBarry } from '~contexts/BarryContext';
import { GlobalState } from '~reducers';

import styles from './Barry.module.scss';

export const Barry = () => {
	const {
		isBarryOpen,
		closeBarry,
		selectedConversationId,
		setSelectedConversationId,
		uiMode,
		startNewChat,
	} = useBarry();
	const [abridgedConversations, setAbridgedConversations] = useState<
		BarrySideNavigationProps['conversations']
	>([]);
	const isMobile = useIsMobile();
	const [showSideNavigation, setShowSideNavigation] = useState<boolean>(false);
	const { isLoggedIn } = useSelector((state: GlobalState) => state?.session);

	const { data: conversationsData, refetch: refetchConversations } =
		useGetConversationsForUser(isLoggedIn && isBarryOpen);

	useEffect(() => {
		if (selectedConversationId) {
			const exists = abridgedConversations.find(
				(c) => c.id === selectedConversationId,
			);
			if (!exists) {
				refetchConversations();
			}
		}
	}, [selectedConversationId, abridgedConversations, refetchConversations]);

	useEffect(() => {
		if (!conversationsData) {
			return;
		}

		const abridged = conversationsData.conversations.map((c) => ({
			id: c.id,
			title: c.title ?? 'Conversation with Barry',
			createdAt: c.createdAt,
		}));
		setAbridgedConversations(abridged);

		if (uiMode === UIModeEnum.CONVERSATION) {
			if (!selectedConversationId) {
				if (abridged.length > 0) {
					setSelectedConversationId(abridged[0].id);
				} else {
					startNewChat();
				}
			} else {
				if (!abridged.some((c) => c.id === selectedConversationId)) {
					refetchConversations();
				}
			}
		}
	}, [
		conversationsData,
		uiMode,
		selectedConversationId,
		setSelectedConversationId,
		startNewChat,
		refetchConversations,
	]);

	return (
		<Popout
			isOpen={isBarryOpen}
			fullHeight={isMobile}
			width={isMobile ? PopoutWidth.Full : PopoutWidth.FortyPercent}
			onClose={closeBarry}
			style={{ zIndex: 100 }}
		>
			<div
				className={classNames(styles.mainBody, {
					[styles.newConversationBackground]:
						uiMode === UIModeEnum.NEW && !showSideNavigation,
				})}
			>
				{!showSideNavigation && (
					<BarryHeader
						onToggleSideNavigation={() =>
							setShowSideNavigation(!showSideNavigation)
						}
					/>
				)}
				{showSideNavigation ? (
					<BarrySideNavigation
						onToggleSideNavigation={() =>
							setShowSideNavigation(!showSideNavigation)
						}
						conversations={abridgedConversations}
					/>
				) : (
					<BarryIndividualConversation />
				)}
			</div>
		</Popout>
	);
};
